import { Button } from "@/components/rac/button";
import { Country } from "@/info/countries";
import { type LoginStep, type OtpMethod } from "@/info/login";
import { LogoWithName } from "@/svgs/logo-with-name";
import { trackEvent } from "@/utils/analytics/track-event";
import Link from "next/link";
import React from "react";
import { CountrySelector } from "./country-selector";
import { OtpInput } from "./otp-input";
import { OtpMethodSelector } from "./otp-method-selector";
import { PhoneInput } from "./phone-input";
import { ResendButton } from "./resend-button";

// Form content component that will be shared between dialog and sheet
export function LoginFormContent({
	step,
	selectedCountry,
	setSelectedCountry,
	phoneNumber,
	setPhoneNumber,
	otpMethod,
	setOtpMethod,
	otp,
	setOtp,
	error,
	isLoading,
	handleSendOTP,
	handleVerifyOTP,
	handleBackToPhone,
	handleResendOTP,
	otpSentAt,
	handleKeyPress,
	isMobile = false,
	isPreferencePage = false,
}: {
	step: LoginStep;
	selectedCountry: Country;
	setSelectedCountry: (country: Country) => void;
	phoneNumber: string;
	setPhoneNumber: (phone: string) => void;
	otpMethod: OtpMethod;
	setOtpMethod: (method: OtpMethod) => void;
	otp: string;
	setOtp: (otp: string) => void;
	error: string;
	isLoading: boolean;
	handleSendOTP: () => void;
	handleVerifyOTP: () => void;
	handleBackToPhone: () => void;
	handleResendOTP: () => void;
	otpSentAt: number | null;
	handleKeyPress: (event: React.KeyboardEvent) => void;
	isMobile?: boolean;
	isPreferencePage?: boolean;
}) {
	const handleSendOTPWithTracking = () => {
		// Track login initiation
		trackEvent({
			facebook: {
				event: "CompleteRegistration",
				options: {
					content_name: "Login Started",
					method: otpMethod,
				},
			},
			google: {
				event: "sign_up",
				options: {
					method: otpMethod,
				},
			},
			matomo: {
				event: "login-start",
				options: {
					category: "auth",
				},
			},
		});

		handleSendOTP();
	};

	const handleVerifyOTPWithTracking = () => {
		// Track login completion
		trackEvent({
			facebook: {
				event: "CompleteRegistration",
				options: {
					content_name: "Login Completed",
					method: otpMethod,
				},
			},
			google: {
				event: "login",
				options: {
					method: otpMethod,
				},
			},
			matomo: {
				event: "login-complete",
				options: {
					category: "auth",
				},
			},
		});

		handleVerifyOTP();
	};

	return (
		<div className={` ${isMobile ? "px-4 pb-4" : "max-w-sm mx-auto"} w-full`}>
			<div className="text-center mb-6">
				{isMobile && !isPreferencePage && <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />}
				<LogoWithName className="h-20 md:h-16 sm:h-8 w-auto mx-auto mb-3 text-primary" />
			</div>

			<div className="text-center mb-6">
				<h2 className={`${isMobile ? "text-lg" : "text-xl md:text-2xl"} font-bold text-gray-900`}>
					{step === "phone" ? "Log in to continue" : "Verify your phone"}
				</h2>
				<p className="text-sm text-gray-600 mt-2">
					{step === "phone"
						? "Enter your phone number to get started"
						: `We sent a code to ${selectedCountry.code} ${phoneNumber}`}
				</p>
			</div>

			<div className="space-y-4" onKeyDown={handleKeyPress}>
				{step === "phone" ? (
					<>
						<CountrySelector
							selectedCountry={selectedCountry}
							onCountryChange={setSelectedCountry}
							isDisabled={isLoading}
						/>

						<PhoneInput
							phoneNumber={phoneNumber}
							onPhoneNumberChange={setPhoneNumber}
							selectedCountry={selectedCountry}
							onEnterPress={handleSendOTPWithTracking}
							isDisabled={isLoading}
						/>

						<OtpMethodSelector
							selectedMethod={otpMethod}
							onMethodChange={setOtpMethod}
							isDisabled={isLoading}
							countryCode={selectedCountry.countryCode}
						/>

						{error && <p className="text-sm text-red-600 text-center">{error}</p>}

						<Button
							className="w-full h-12 font-semibold"
							onPress={handleSendOTPWithTracking}
							isDisabled={isLoading}
							variant={"default"}
						>
							{isLoading ? (
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
									<span>Sending...</span>
								</div>
							) : (
								"Send OTP"
							)}
						</Button>
					</>
				) : (
					<>
						<OtpInput
							otp={otp}
							onOtpChange={setOtp}
							onEnterPress={handleVerifyOTPWithTracking}
							isDisabled={isLoading}
						/>

						{error && <p className="text-sm text-red-600 text-center">{error}</p>}

						<Button
							className="w-full h-12 font-semibold"
							onPress={handleVerifyOTPWithTracking}
							isDisabled={isLoading}
							variant={"default"}
						>
							{isLoading ? (
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
									<span>Verifying...</span>
								</div>
							) : (
								"Verify OTP"
							)}
						</Button>

						<div className="flex flex-col sm:flex-row gap-2 pt-2">
							<Button
								variant="outline"
								className="flex-1 h-10 text-sm border-red-200 text-red-600 hover:bg-red-50 touch-manipulation"
								onPress={handleBackToPhone}
								isDisabled={isLoading}
							>
								Back
							</Button>
							<ResendButton
								onResend={handleResendOTP}
								isLoading={isLoading}
								initialTimer={otpSentAt ? Math.max(0, 30 - Math.floor((Date.now() - otpSentAt) / 1000)) : 0}
								className="flex-1 h-10 text-sm border-red-200 text-red-600 hover:bg-red-50 touch-manipulation disabled:opacity-50"
							/>
						</div>
					</>
				)}
			</div>

			<div className="mt-6 text-center">
				<p className="text-xs text-gray-500">
					By continuing, you agree to our{" "}
					<Link href="/terms" className="text-red-600 hover:underline">
						Terms of Service
					</Link>{" "}
					and{" "}
					<Link href="/privacy-policy" className="text-red-600 hover:underline">
						Privacy Policy
					</Link>
				</p>
			</div>
		</div>
	);
}
